import { memo, useContext, useEffect, useMemo, useState } from "react";
import { progressSlider } from "../CommonForm/CommonFormInterface";
import { CommonFormContext } from "../MainComponent/MainComponent";

const ProgressSlider = ({
  field,
  value,
}: {
  field: progressSlider;
  value: any;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const {
    style = {},
    progressStyle = {},
    dotStyle = {},
    leftLabelPrefix = "",
    leftLabelPostfix = "",
    rightLabelPrefix = "",
    rightLabelPostfix = "",
    minValue = 1,
    className = "",
    maxValue = 15,
  } = field;

  const extractedLeftLabel = `${
    leftLabelPrefix ? leftLabelPrefix : ""
  }${minValue}${leftLabelPostfix ? leftLabelPostfix : ""}`;
  const extractedRightLabel = `${
    rightLabelPrefix ? rightLabelPrefix : ""
  }${maxValue}${rightLabelPostfix ? rightLabelPostfix : ""}`;
  const context = useContext(CommonFormContext);

  const calculateProgress = useMemo((): number => {
    return ((value - minValue) / (maxValue - minValue)) * 100;
  }, [value, minValue, maxValue]);

  const calculateNewValue = (
    e: MouseEvent | TouchEvent,
    element: HTMLElement
  ): number => {
    const rect = element.getBoundingClientRect();
    const x = "clientX" in e ? e.clientX : e.touches?.[0]?.clientX || 0;
    const percentage = Math.min(Math.max((x - rect.left) / rect.width, 0), 1);
    return Math.round(minValue + percentage * (maxValue - minValue));
  };

  const updateValue = (
    e: MouseEvent | TouchEvent,
    element: HTMLElement
  ): void => {
    const newValue = calculateNewValue(e, element);
    const val = Math.min(Math.max(newValue, minValue), maxValue);

    context.handleOnChange(
      { target: { value: val } },
      field?.properties?.onChange?.actions
    );
  };

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    setIsDragging(true);
    updateValue(e.nativeEvent, e.currentTarget.parentElement as HTMLElement);
  };

  const handleMouseMove = (e: MouseEvent): void => {
    if (isDragging) {
      const sliderElement = document.querySelector('[data-slider="true"]');
      if (sliderElement instanceof HTMLElement) {
        updateValue(e, sliderElement);
      }
    }
  };

  const handleMouseUp = (): void => {
    setIsDragging(false);
  };

  // Touch events
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>): void => {
    setIsDragging(true);
    updateValue(e.nativeEvent, e.currentTarget.parentElement as HTMLElement);
  };

  const handleTouchMove = (e: TouchEvent): void => {
    if (isDragging) {
      e.preventDefault();
      const sliderElement = document.querySelector('[data-slider="true"]');
      if (sliderElement instanceof HTMLElement) {
        updateValue(e, sliderElement);
      }
    }
  };

  const handleTouchEnd = (): void => {
    setIsDragging(false);
  };

  // Click event
  const handleSliderClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!isDragging) {
      updateValue(e.nativeEvent, e.currentTarget);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging]);

  const getLeftLabel = useMemo(() => {
    return extractedLeftLabel;
  }, [extractedLeftLabel]);

  const getRightLabel = useMemo(() => {
    return extractedRightLabel;
  }, [extractedRightLabel]);

  return (
    <div className={`slider-container ${className}`} style={style}>
      <div
        data-slider="true"
        className="slider-track"
        onClick={handleSliderClick}
      >
        <div
          className="slider-progress"
          style={{
            width: `${calculateProgress}%`,
            ...progressStyle,
          }}
        />

        <div
          className={`slider-dot ${isDragging ? "dragging" : ""}`}
          style={{
            left: `${calculateProgress}%`,
            ...dotStyle,
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
      </div>

      <div className="slider-labels">
        <span>{getLeftLabel || `${minValue}`}</span>
        <span>{getRightLabel || `${maxValue}`}</span>
      </div>
    </div>
  );
};

export default memo(ProgressSlider);
